import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@repo/common/utils/cn";
import { textBodyVariants } from "./text";
import { _disabledPointer, _focus } from "./shared-styles";

/**
 * Base styles
 */
const _base =
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg gap-1 px-2";
// const _typography = "text-sm font-medium ";
const _typography = textBodyVariants();
const _rest = "transition-colors";

/**
 * Variants
 * #TODO: add semantic text vars
 */

// Fill
const fill =
  "bg-action-primary text-action-primary-inverse hover:bg-action-primary-hover active:bg-action-primary-hover";
const fillAction =
  "bg-action-primary text-action-primary-inverse hover:bg-action-primary-hover active:bg-action-primary-hover";
const fillDanger =
  "bg-action-destructive text-white hover:bg-status-error-darken-1 active:bg-status-error-darken-2";

const secondary =
  "bg-status-neutral-lighten-2 text-foreground border border-solid border-status-neutral-lighten-1 hover:bg-status-neutral-lighten-1 active:bg-status-neutral-lighten-1";

// Outline
const outline =
  "bg-background text-foreground border border-solid border-border hover:bg-background-muted active:bg-background";
const outlineAction =
  "bg-secondary text-secondary-foreground border border-solid border-action-primary hover:bg-secondary-hover active:bg-secondary-hover";
const outlineDanger =
  "bg-status-error-lighten-3 text-status-error-darken-2 border border-solid border-action-destructive hover:bg-status-error-lighten-2 active:bg-status-error-lighten-1";

// Ghost
const ghost =
  "bg-transparent text-foreground hover:bg-status-neutral-lighten-2 active:bg-status-neutral-lighten-3";
const ghostAction =
  "bg-transparent text-action-primary hover:bg-primary-lighten-3 active:bg-primary-lighten-2";
const ghostDanger =
  "bg-transparent text-action-destructive hover:bg-status-error-lighten-3 active:bg-status-error-lighten-2";

const buttonVariants = cva(
  [_base, _typography, _focus, _disabledPointer, _rest],
  {
    variants: {
      variant: {
        fill,
        outline,
        ghost,
        secondary,
      },
      intent: {
        default: "",
        action: "",
        danger: "",
        secondary: "",
      },
      size: {
        auto: "h-auto py-1.5 gap-2",
        md: "h-12 md:h-9 py-1.5 gap-2",
        sm: "h-7 py-1 gap-1 ",
      },
    },
    compoundVariants: [
      {
        variant: "fill",
        intent: "action",
        class: fillAction,
      },
      {
        variant: "fill",
        intent: "danger",
        className: fillDanger,
      },
      {
        variant: "fill",
        intent: "secondary",
        className: secondary,
      },
      {
        variant: "ghost",
        intent: "action",
        class: ghostAction,
      },
      {
        variant: "ghost",
        intent: "danger",
        className: ghostDanger,
      },
      {
        variant: "outline",
        intent: "action",
        className: outlineAction,
      },
      {
        variant: "outline",
        intent: "danger",
        className: outlineDanger,
      },
    ],
    defaultVariants: {
      variant: "fill",
      size: "md",
      intent: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & React.ComponentProps<"button">
>(({ className, variant, size, intent, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, intent, className }))}
      {...props}
    />
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };
